import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Experience, Review, WhatsIncluded, ScheduleItem } from "@/types/experience";

// Helper function to parse reviews from JSONB
function parseReviews(reviewsData: unknown): Review[] {
  if (!reviewsData || !Array.isArray(reviewsData)) return [];
  return reviewsData.map((review: any) => ({
    ...review,
    rating: Number(review.rating),
  }));
}

export function useExperiences(category?: string, searchQuery?: string) {
  return useQuery({
    queryKey: ["experiences", category, searchQuery],
    queryFn: async () => {
      let query = supabase
        .from("experiences")
        .select("*")
        .order("rating", { ascending: false });

      if (category && category !== "All Vibes") {
        query = query.eq("category", category);
      }

      const { data, error } = await query;
      if (error) throw error;

      let experiences = (data || []).map(exp => ({
        ...exp,
        price_per_person: Number(exp.price_per_person),
        service_fee: Number(exp.service_fee),
        rating: Number(exp.rating),
        whats_included: (exp.whats_included as unknown as WhatsIncluded[]) || [],
        schedule: (exp.schedule as unknown as ScheduleItem[]) || [],
        reviews: parseReviews(exp.reviews),
      })) as Experience[];

      // Filter by search query (title and description)
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        experiences = experiences.filter(
          exp =>
            exp.title.toLowerCase().includes(query) ||
            exp.description.toLowerCase().includes(query)
        );
      }

      return experiences;
    },
  });
}

export function useExperience(id: string) {
  return useQuery({
    queryKey: ["experience", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("experiences")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      return {
        ...data,
        price_per_person: Number(data.price_per_person),
        service_fee: Number(data.service_fee),
        rating: Number(data.rating),
        whats_included: (data.whats_included as unknown as WhatsIncluded[]) || [],
        schedule: (data.schedule as unknown as ScheduleItem[]) || [],
        reviews: parseReviews(data.reviews),
      } as Experience;
    },
    enabled: !!id,
  });
}

export function useExperienceBySlug(slug: string) {
  return useQuery({
    queryKey: ["experience", "slug", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("experiences")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) throw error;

      return {
        ...data,
        price_per_person: Number(data.price_per_person),
        service_fee: Number(data.service_fee),
        rating: Number(data.rating),
        whats_included: (data.whats_included as unknown as WhatsIncluded[]) || [],
        schedule: (data.schedule as unknown as ScheduleItem[]) || [],
        reviews: parseReviews(data.reviews),
      } as Experience;
    },
    enabled: !!slug,
  });
}
